import {baseURL} from '@/store/settings';
import api from '@/api';

const params = (new URL(document.location)).searchParams;
const isTest = params.get('test');

const parametricUrl = isTest ? '/api/estate-search/test/parametric' : '/api/estate-search/results';

export default {
    namespaced: true,

    state: {
        /* объект данных по квартирам в параметрическом */
        premises    : {},
        /* массив квартир для печати */
        printFlats  : [],
        /* обрабатываемая форма с фильтрами */
        form        : null,
        /* данные фильтров */
        formData    : null,
        /* происходит ли сейчас загрузка */
        isProcessing: false,
        /* текущий активный раздел */
        activeType  : '',
        /* категории */
        categories  : [],
        /* пагинация */
        pagination  : {},
        /* фильтры */
        filters     : {},
        /* текущая выбранная сортировка */
        sortCurrent : {},
        /* элементы сортировки */
        sortItems   : {}
    },

    getters: {
        /**
         * Метод отдает помещения для печати
         * @param {Object} state - текущий state.
         * @returns {Array} - массив помещений для печати.
         */
        GET_PRINT_FLATS(state) {
            return state.printFlats;
        },

        /**
         * Метод отдает id-шники помещений для печати
         * @param {Object} state - текущий state.
         * @returns {Array} - массив id-шников помещений для печати.
         */
        GET_PRINT_FLATS_IDS(state) {
            return state.printFlats.map((flat) => flat.id);
        },

        /**
         * Метод отдает текущую форму фильтров на странице
         * @param {Object} state - текущий state.
         * @returns {HTMLFormElement} - форма
         */
        GET_FORM(state) {
            return state.form;
        },

        /**
         * Метод отдает данные формы фильтров на странице
         * @param {Object} state - текущий state.
         * @returns {FormData} - данные формы
         */
        GET_FORM_DATA(state) {
            return state.formData;
        },

        /**
         * Метод отдает статус загрузки/подгрузки
         * @param {Object} state - текущий state.
         * @returns {Boolean} - проиходит/не происходит сейчас загрузка
         */
        GET_IS_PROCESSING(state) {
            return state.isProcessing;
        },

        /**
         * Метод отдает объект помещений по типам
         * @param {Object} state - текущий state.
         * @returns {Object} - помещения
         */
        GET_PREMISES(state) {
            return state.premises;
        },

        /**
         * Метод отдает массив категорий помещений
         * @param {Object} state - текущий state.
         * @returns {Array} - массив категорий
         */
        GET_CATEGORIES(state) {
            return state.categories;
        },

        /**
         * Метод отдает текущий активный тип показываемых помещений
         * @param {Object} state - текущий state.
         * @returns {String} - тип помещений
         */
        GET_ACTIVE_TYPE(state) {
            return state.activeType;
        },

        /**
         * Метод отдает текущий объект пагинаций разбитый по типам
         * @param {Object} state - текущий state.
         * @returns {String} - доступные пагинации
         */
        GET_PAGINATION(state) {
            return state.pagination;
        },

        /**
         * Метод отдает текущий объект фильтров разбитый по типам
         * @param {Object} state - текущий state.
         * @returns {String} - доступные фильтры
         */
        GET_FILTERS(state) {
            return state.filters;
        },

        /**
         * Метод отдает параметры текущей сортировки разбитый по типам
         * @param {Object} state - текущий state.
         * @returns {Object} - текущее значение параметра.
         */
        GET_CURRENT_SORT(state) {
            return state.sortCurrent;
        },

        /**
         * Метод отдает объект фильтров разбитый по типам
         * @param {Object} state - текущий state.
         * @returns {String} - доступные фильтры по типам
         */
        GET_SORT_ITEMS(state) {
            return state.sortItems;
        }
    },

    actions: {
        /**
         * Событие происходит при инициализации выборщика.
         * @param {Object} context - текущий контекст.
         * @param {Object} body - тело запроса.
         */
        init({commit, dispatch, getters}, body) {
            const url =  parametricUrl;
            const config = {
                url,
                baseURL
            };

            // Сохраняем sort/order из URL-параметров до того, как SET_FORM_DATA удалит presetParams
            const presetSort = body.presetParams?.sort && body.presetParams?.order
                ? { type: body.presetParams.sort, order: body.presetParams.order }
                : null;

            commit('SET_IS_PROCESSING', true);
            commit('SET_FORM_DATA', body);

            return api.post(url, getters.GET_FORM_DATA, config)
                .then((response) => {
                    const status = response.data.status;
                    const errors = response.data.messages;

                    if (!status) {
                        throw new Error(errors.length ? errors : 'Ошибка на сервере');
                    }

                    if (errors && errors.length) {
                        errors.forEach((error) => {
                            console.error(error);
                        });
                    }

                    const data = response.data;

                    dispatch('setCategories', data);
                    commit('SET_PAGINATION', {
                        category  : data?.settings?.type || '',
                        pagination: data?.pagination || null
                    });
                    commit('SET_ELEMENTS', {
                        category: data?.settings?.type,
                        premises: data?.premises
                    });
                    commit('SET_FILTERS', {
                        category: data?.settings?.type,
                        filters : data?.filters
                    });
                    commit('SET_CURRENT_SORT', {
                        category: data?.settings?.type,
                        sortData: presetSort || data?.settings?.sort?.default
                    });
                    commit('SET_SORT_ITEMS', {
                        category: data?.settings?.type,
                        items   : data?.settings?.sort?.items || []
                    });
                    commit('SET_ACTIVE_TYPE', data?.settings?.type || '');
                })
                .catch((error) => {
                    console.error(`При инициализации параметрического поиска произошла ошибка: ${error}`);
                })
                .finally(() => {
                    commit('SET_IS_PROCESSING', false);
                });
        },

        /**
         * Событие происходит при инициализации новой категории.
         * @param {Object} context - текущий контекст.
         * @param {Object} body - тело запроса.
         */
        initCategory({commit, dispatch, getters}, body) {
            const url =  parametricUrl;
            const config = {
                url,
                baseURL
            };

            commit('SET_IS_PROCESSING', true);
            commit('SET_FORM_DATA', body);

            return api.post(url, getters.GET_FORM_DATA, config)
                .then((response) => {
                    const status = response.data.status;
                    const errors = response.data.messages;

                    if (!status) {
                        throw new Error(errors.length ? errors : 'Ошибка на сервере');
                    }

                    if (errors && errors.length) {
                        errors.forEach((error) => {
                            console.error(error);
                        });
                    }

                    const data = response.data;

                    // dispatch('setCategories', data);
                    commit('SET_PAGINATION', {
                        category  : body.category || '',
                        pagination: data?.pagination || null
                    });
                    commit('SET_ELEMENTS', {
                        category: body.category,
                        premises: data?.premises
                    });
                    commit('SET_FILTERS', {
                        category: body.category,
                        filters : data?.filters
                    });
                    commit('SET_CURRENT_SORT', {
                        category: body.category,
                        sortData: data?.settings?.sort?.default
                    });
                    commit('SET_SORT_ITEMS', {
                        category: body.category,
                        items   : data?.settings?.sort?.items || []
                    });
                    commit('SET_ACTIVE_TYPE', body.category || '');
                })
                .catch((error) => {
                    console.error(`При инициализации параметрического поиска произошла ошибка: ${error}`);
                })
                .finally(() => {
                    commit('SET_IS_PROCESSING', false);
                });
        },

        /**
         * Событие происходит при смене фильтров
         * @param {Object} context - текущий контекст.
         * @param {HTMLFormElement} form - форма.
         * @param {String} type - тип запроса
         */
        changeFilter({commit, getters}, {form, type}) {
            const url =  parametricUrl;
            const config = {
                url,
                baseURL
            };

            const activeType = getters.GET_ACTIVE_TYPE;
            const sortData = getters.GET_CURRENT_SORT[activeType];

            commit('SET_FORM', form);
            commit('SET_FORM_DATA', {
                type,
                category: activeType,
                ...(sortData?.type && { sort: sortData.type }),
                ...(sortData?.order && { order: sortData.order })
            });
            commit('SET_IS_PROCESSING', true);

            return api.post(url, getters.GET_FORM_DATA, config)
                .then((response) => {
                    const data = response.data;

                    commit('SET_PAGINATION', {
                        category  : data?.settings.type,
                        pagination: data?.pagination || null
                    });
                    commit('SET_ELEMENTS', {
                        category: data?.settings.type,
                        premises: data?.premises
                    });
                    commit('SET_FILTERS', {
                        category: data?.settings.type,
                        filters : data?.filters
                    });
                })
                .catch((error) => {
                    console.error(`При фильтрации произошла ошибка= ${error}`);
                })
                .finally(() => {
                    commit('SET_IS_PROCESSING', false);
                });
        },

        /**
         * Событие происходит при смене страницы через пагинацию
         * @param {Object} context - текущий контекст.
         * @param {String} type - тип запроса
         * @param {String} category - текущая категория показываемых помещений
         * @param {Number} page - номер страницы
         */
        changePage({commit, getters}, {type, category, page}) {
            const sortData = getters.GET_CURRENT_SORT;

            commit('SET_IS_PROCESSING', true);
            commit('SET_FORM_DATA', {
                type,
                category,
                page,
                sort : sortData[category]?.type || '',
                order: sortData[category]?.order || ''
            });
        },

        /**
         * Событие происходит при смене типа или направления сортировки.
         * @param {Object} context - текущий контекст.
         * @param {String} category - выбранная категория
         * @param {Object} sortData - данные сортировки.
         */
        changeSort({commit}, { category, sortData }) {
            commit('SET_IS_PROCESSING', true);
            commit('SET_CURRENT_SORT', { category, sortData });
            commit('SET_FORM_DATA', {
                category,
                type : 'sort',
                page : 1,
                sort : sortData.type,
                order: sortData.order
            });
        },

        /**
         * Действие отсылает запрос за данными при переключении пагинации/сортировки
         * @param {Function} commit - функция вызова мутаций
         * @param {Boolean} sort - флаг сортировки
         */
        sendSortAndPaginationRequest({commit, getters}, sort = false) {
            const url =  parametricUrl;
            const config = {
                url,
                baseURL
            };

            return api.post(url, getters.GET_FORM_DATA, config)
                .then((response) => {
                    const status = response.data.status;
                    const errors = response.data.messages;

                    if (!status) {
                        throw new Error(errors.length ? errors : 'Ошибка на сервере');
                    }

                    if (errors && errors.length) {
                        errors.forEach((error) => {
                            console.error(error);
                        });
                    }

                    const data = response.data;

                    if (!sort) {
                        commit('SET_PAGINATION', {
                            category  : data?.settings?.type || '',
                            pagination: data?.pagination || null
                        });
                    }

                    commit('SET_ELEMENTS', {
                        category: data?.settings?.type || '',
                        premises: data?.premises
                    });
                })
                .catch((error) => {
                    console.error(`При инициализации параметрического поиска произошла ошибка: ${error}`);
                })
                .finally(() => {
                    commit('SET_IS_PROCESSING', false);
                });
        },

        /**
         * Устанавливает доступные категории (табы)
         * @param {Object} context - текущий контекст.
         * @param {Object} data - данные с сервера
         */
        setCategories({commit, getters}, data) {
            let categories = [];

            if (data?.categories) {
                categories = data.categories.map((item) => {
                    // const countToPrint = [
                    //     ...getters.GET_PRINT_FLATS.filter((printFlat) => printFlat.type === item.name) || []
                    // ].length;

                    return {
                        id   : item.name,
                        name : item.title,
                        count: 0
                    };
                });
            }

            commit('SET_CATEGORIES', categories);
        },

        /**
         * Устанавливает количество выбранных квартир в категории
         * @param {Object} context - текущий контекст.
         */
        changeCountInCategories({commit, getters}) {
            const categories = JSON.parse(JSON.stringify(getters.GET_CATEGORIES));

            categories.forEach((tab) => {
                tab.count = [...getters.GET_PRINT_FLATS.filter((printFlat) => printFlat.type === tab.id) || []].length;
            });

            commit('SET_CATEGORIES', categories);
        },

        /**
         * Устанавливает тип показываемых помещений
         * @param {Object} context - текущий контекст.
         * @param {String} type - новый тип
         */
        changeActiveType({commit}, type) {
            commit('SET_ACTIVE_TYPE', type);
        },

        /**
         * Добавляет/удаляет помещений из списка на печать
         * @param {Object} context - текущий контекст.
         * @param flatData {Object} - данные помещения
         */
        addFlatToPrint({state, commit, dispatch}, flatData) {
            const printFlats = [...state.printFlats];
            const flatIndex = printFlats.findIndex((item) => item.id === flatData.id);

            if (flatIndex === -1) {
                printFlats.unshift(flatData);
            } else {
                printFlats.splice(flatIndex, 1);
            }

            commit('SET_PRINT_FLATS', printFlats);
            dispatch('changeCountInCategories');
        },

        /**
         * Удаляет помещение из списка на печать
         * @param {Object} context - текущий контекст.
         * @param id {String} - id помещения
         */
        removeFlatFromPrint({state, commit, dispatch}, id) {
            const printFlats = [...state.printFlats];
            const flatIndex = printFlats.findIndex((item) => item.id === id);

            if (flatIndex !== -1) {
                printFlats.splice(flatIndex, 1);
            }

            commit('SET_PRINT_FLATS', printFlats);
            dispatch('changeCountInCategories');
        },

        /**
         * Удаляет все помещения из печати
         * @param {Object} context - текущий контекст.
         */
        removeAllFlatsFromPrint({commit, dispatch}) {
            commit('SET_PRINT_FLATS', []);
            dispatch('changeCountInCategories');
        },

        /**
         * Формирует пдф выбранных помещений
         * @param {Object} context - текущий контекст.
         * @param {FormData} formData - данные формы
         */
        sendToPrint({ state }, formData) {
            const url = '/api/estate/pdf';
            const config = {
                headers: {
                    'content-type': 'multipart/form-data'
                },
                url,
                baseURL
            };

            return api.post(url, formData, config)
                .then((response) => {
                    return response;
                })
                .catch((error) => {
                    throw error;
                });
        }
    },

    mutations: {
        /**
         * Мутация устанавливает состояние загрузки.
         * @param {Object} state - текущий state.
         * @param {Boolean} value - значение состояния "загрузка".
         */
        SET_IS_PROCESSING(state, value) {
            state.isProcessing = value;
        },

        /**
         * Мутация устанавливает текущую сортировку помещений.
         * @param {Object} state - текущий state.
         * @param {String} category - категория в которой устанавливаем сортировку
         * @param {Object} sortData - данные сортировки
         */
        SET_CURRENT_SORT(state, { category, sortData }) {
            state.sortCurrent[category] = {};
            state.sortCurrent[category] = {
                type : sortData.type,
                order: sortData.order
            };
        },

        /**
         * Мутация устанавливает доступные сортировки для помещений.
         * @param {Object} state - текущий state.
         * @param {String} category - категория в которой устанавливаем сортировку
         * @param {Object} items - данные сортировок
         */
        SET_SORT_ITEMS(state, {category, items }) {
            state.sortItems[category] = {};
            state.sortItems[category] = items;
        },

        /**
         * Мутация устанавливает текущую форму со страницы
         * @param {Object} state - текущий state.
         * @param {HTMLFormElement} form - форма
         */
        SET_FORM(state, form) {
            state.form = form;
        },

        /**
         * Мутация устанавливает данные формы
         * @param {Object} state - текущий state.
         * @param {Object} body - данные запроса вне формы
         */
        SET_FORM_DATA(state, body) {
            const presetParams = body.presetParams;

            if (presetParams !== undefined) {
                delete body.presetParams;
            }

            if (body.clear) {
                state.formData = new FormData();

                delete body.clear;
            } else if (presetParams) {
                // Собираем FormData из готовых параметров (например, из URL)
                state.formData = new FormData();

                Object.entries(presetParams).forEach(([key, val]) => {
                    if (Array.isArray(val)) {
                        val.forEach(v => state.formData.append(key, v));
                    } else {
                        state.formData.append(key, val);
                    }
                });
            } else {
                const form = state.form;

                state.formData = form ? new FormData(form) : new FormData();
            }

            Object.keys(body).forEach((key) => {
                state.formData.append(key, body[key]);
            });
        },

        /**
         * Мутация устанавливает помещения в категории
         * @param {Object} state - текущий state.
         * @param {String} category - категория
         * @param {Array} premises - помещения
         */
        SET_ELEMENTS(state, { category, premises }) {
            state.premises[category] = [];
            state.premises[category] = premises;
        },

        /**
         * Мутация устанавливает доступные категории помещений (табы)
         * @param {Object} state - текущий state.
         * @param {Array} categories - категории
         */
        SET_CATEGORIES(state, categories) {
            state.categories = categories;
        },

        /**
         * Мутация устанавливает тип показываемых помещений
         * @param {Object} state - текущий state.
         * @param {String} type - тип
         */
        SET_ACTIVE_TYPE(state, type) {
            state.activeType = type;
        },

        /**
         * Мутация устанавливает фильтры в категории
         * @param {Object} state - текущий state.
         * @param {String} category - категория
         * @param {Array} filters - фильтры
         */
        SET_FILTERS(state, {category, filters}) {
            state.filters[category] = [];
            state.filters[category] = filters;
            // const lastfilter = state.filters[category][state.filters[category].length - 1];
            // state.filters[category].pop();
            // state.filters[category].push({
            //     type          : 'checkbox-w-title',
            //     category      : 'master-bedroom',
            //     view          : 'chips',
            //     label         : 'Мастер-спальня',
            //     width         : 'full',
            //     negativeMargin: true,
            //     items         : [
            //         {
            //             id      : 12,
            //             value   : 12,
            //             name    : 'Мастер-спальня с гардеробной',
            //             title   : 'Мастер-спальня с гардеробной',
            //             checked : false,
            //             disabled: false
            //         },
            //         {
            //             id      : 13,
            //             value   : 13,
            //             name    : 'Мастер-спальня с проходной гардеробной',
            //             title   : 'Мастер-спальня с проходной гардеробной',
            //             checked : false,
            //             disabled: false
            //         },
            //         {
            //             id      : 14,
            //             value   : 14,
            //             name    : 'Мастер-спальня с с/у',
            //             title   : 'Мастер-спальня с с/у',
            //             checked : false,
            //             disabled: false
            //         },
            //         {
            //             id      : 15,
            //             value   : 15,
            //             name    : 'Мастер-спальня с с/у и гардеробной',
            //             title   : 'Мастер-спальня с с/у и гардеробной',
            //             checked : false,
            //             disabled: false
            //         },{
            //             id      : 16,
            //             value   : 16,
            //             name    : 'Мастер-спальня с с/у и проходной гардеробной',
            //             title   : 'Мастер-спальня с с/у и проходной гардеробной',
            //             checked : false,
            //             disabled: false
            //         }
            //     ]
            // });
            // state.filters[category].push(lastfilter);
        },

        /**
         * Мутация устанавливает помещения для печати
         * @param {Object} state - текущий state.
         * @param {Array} flats - помещения
         */
        SET_PRINT_FLATS(state, flats) {
            state.printFlats = flats;
        },

        /**
         * Мутация устанавливает пагинацию в категории
         * @param {Object} state - текущий state.
         * @param {String} category - категория
         * @param {Object} pagination - пагинация
         */
        SET_PAGINATION(state, {category, pagination}) {
            state.pagination[category] = {};
            state.pagination[category] = pagination;
        }
    }
};
